import { useState } from 'react';

import { randomUUID } from 'expo-crypto';
import { useMinimalCart } from 'features/cart/hooks/use-minimal-cart';
import { useUtensils } from 'features/cart/hooks/use-utensils';
import { ICartEntry, MinimalCartEntryType } from 'features/cart/types';
import { StyleSheet, View } from 'react-native';
import { Pressable } from 'react-native';
import Animated, { FadeInUp, FadeOutUp } from 'react-native-reanimated';
import { Icon } from 'ucl/components/icon/Icon';
import { ListItemCheckboxGroup } from 'ucl/components/list-item-checkbox-group';

import SanityBlockRenderer from 'components/sanity-block-renderer';
import { Skeleton } from 'components/ui/skeleton';
import useFoodware from 'hooks/use-foodware';
import { t } from 'state/intl/translations';
import { useServiceModeContext } from 'state/service-mode';
import { getDesignToken, primitive } from 'styles/constants';
import { emptySanityImage } from 'utils/sanity';

import { logCartAddToCart } from '../../utils/cart-analytics';
import { CartSectionHeader } from '../cart-section-header';

const FoodwareSectionSkeleton = () => {
  return (
    <View>
      <Skeleton shape="rectangle" style={{ width: '100%', height: 96, marginVertical: 0 }} />
    </View>
  );
};

export const FoodwareSection = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { featureFoodware, loading: foodwareLoading } = useFoodware();
  const { utensils, loading: utensilsLoading } = useUtensils();
  const loading = foodwareLoading || utensilsLoading;

  const { cartEntries, addItemToMinimalCart, removeItemFromMinimalCart } = useMinimalCart();
  const { serviceMode } = useServiceModeContext();

  const selectedItems = cartEntries
    .filter(entry => entry.type === MinimalCartEntryType.extra)
    .map(entry => entry.id);

  const listItems = utensils?.map(utensil => ({
    value: utensil.id,
    label: utensil.name + (utensil.isAvailable ? '' : ` (${t('unavailable')})`),
    accessibilityLabel: utensil.name,
    disabled: !utensil.isAvailable,
  }));

  const handleChange = (nextValues: string[]) => {
    const previousCartEntries: ICartEntry[] = cartEntries.filter(
      entry => entry.type !== MinimalCartEntryType.extra
    );

    cartEntries.forEach(entry => {
      if (entry.type === MinimalCartEntryType.extra) {
        removeItemFromMinimalCart(entry);
      }
    });
    nextValues.forEach(value => {
      const utensil = utensils?.find(utensil => utensil.id === value);
      const newUtensilEntry = {
        id: value,
        cartId: randomUUID(),
        quantity: 1,
        name: utensil?.name ?? '',
        image: emptySanityImage(),
        url: '',
        type: MinimalCartEntryType.extra,
        children: [],
      };

      if (serviceMode) {
        logCartAddToCart({
          cartEntry: newUtensilEntry,
          previousCartEntries,
          serviceMode,
        });
      }

      addItemToMinimalCart(newUtensilEntry);
      previousCartEntries.push(newUtensilEntry);
    });
  };

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  if (loading) {
    return null;
  }
  if (!featureFoodware?.items?.length) {
    return <FoodwareSectionSkeleton />;
  }

  return (
    <Animated.View style={styles.container} testID="foodware">
      <CartSectionHeader
        title={featureFoodware?.title?.locale ?? ''}
        testID="cart-section-heading-label"
      />
      <Pressable style={styles.headerContainer} onPress={handleToggle}>
        <SanityBlockRenderer maxWidth="90%" content={featureFoodware?.description?.localeRaw} />
        <Icon variant={isExpanded ? 'collapse' : 'select'} />
      </Pressable>
      {isExpanded && (
        <Animated.View entering={FadeInUp.duration(200)} exiting={FadeOutUp.duration(200)}>
          <ListItemCheckboxGroup
            value={selectedItems}
            onChange={handleChange}
            listItems={listItems ?? []}
          />
        </Animated.View>
      )}
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 16,
    backgroundColor: getDesignToken('background-card'),
    borderRadius: primitive.$borderRadius,
    overflow: 'hidden',
  },
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: getDesignToken('background-card'),
    borderRadius: primitive.$borderRadius,
  },
});
